import { ExternalVerificationStatus, RequestStatus } from '@app/src/types/resourceExplorer'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'

/**
 * Maps request status values to their corresponding verification status values.
 * This is pure business logic that determines the relationship between request and verification statuses.
 */
export function mapRequestStatusToVerificationStatus(requestStatus: RequestStatus): ExternalVerificationStatus | null {
  switch (requestStatus) {
    case RequestStatus.ResponseApproved:
      return ExternalVerificationStatus.Approved
    case RequestStatus.ResponseCorrectionNeeded:
      return ExternalVerificationStatus.CorrectionNeeded
    default:
      return null
  }
}

/**
 * Checks if the given status values include the ResponseSubmitted status.
 * ResponseSubmitted represents responses that have not been reviewed yet.
 */
export function includesSubmittedStatus(statusValues: unknown[]): boolean {
  return statusValues.includes(RequestStatus.ResponseSubmitted)
}

/**
 * Extracts verification status values from request status values.
 * Only returns verification statuses for request statuses that have been reviewed (Approved or CorrectionNeeded).
 */
export function extractVerificationStatuses(requestStatusValues: unknown[]): ExternalVerificationStatus[] {
  return requestStatusValues
    .filter(status => status === RequestStatus.ResponseCorrectionNeeded || status === RequestStatus.ResponseApproved)
    .map(status => mapRequestStatusToVerificationStatus(status as RequestStatus))
    .filter((status): status is ExternalVerificationStatus => status !== null)
}

/**
 * Enhances API filters by adding verification status filter when appropriate.
 *
 * Business rule: When filtering by request status for reviewed responses (Approved or CorrectionNeeded),
 * we also need to filter by the corresponding verification status. However, if the filter includes
 * "Submitted" status (not reviewed yet), we should not add the verification filter since those
 * responses don't have verification records.
 *
 * @param apiFilters - The original filters from the API/UI
 * @returns Enhanced filters with verification status added when appropriate
 */
export function enhanceFiltersWithVerificationStatus(apiFilters: FilterGroup[]): FilterGroup[] {
  const enhancedFilters = [...apiFilters]
  const requestStatusFilter = apiFilters.find(filter => filter.name === 'request.requestStatus')

  if (!requestStatusFilter) {
    return enhancedFilters
  }

  const statusValues = requestStatusFilter.filters.map(f => f.value).flat()
  const verificationStatuses = extractVerificationStatuses(statusValues)
  const hasSubmittedStatus = includesSubmittedStatus(statusValues)

  // Only add verification filter if we have verification statuses and no submitted status
  if (!hasSubmittedStatus && verificationStatuses.length > 0) {
    enhancedFilters.push({
      name: 'verifications.externalVerificationStatus',
      filters: [
        {
          operator: Operators.In,
          value: verificationStatuses,
        },
      ],
    })
  }

  return enhancedFilters
}

/**
 * Creates a filter to exclude AI-screened responses.
 * This is used as an implicit filter in the responses list.
 */
export function createNonScreenedResponseFilter(): FilterGroup {
  return {
    name: 'request.requestStatus',
    filters: [
      {
        operator: Operators.NotIn,
        value: [RequestStatus.ResponseScraped],
      },
    ],
  }
}
